import { fetchMe } from "@/store/auth.actions";
import useAuth from "@/store/auth.slice";
import { defaultUserInfo } from "@/types/user/userInfo";
import {
  readSsoReturnParams,
  SSO_CODE_PARAM,
  SSO_ERROR_PARAM,
} from "./ssoReturnUtils";

/** Pathname + search, no origin. */
export const formatLastVisitedLocation = (
  pathname: string,
  search: string,
): string => `${pathname}${search}`;

export const shouldPersistLastVisitedLocation = (location: string): boolean => {
  if (!location.startsWith("/")) return false;
  if (location.startsWith("/auth")) return false;
  if (location.startsWith("/briefing")) return false;
  return true;
};

/** Fallback when no valid stored location exists. */
export const DEFAULT_AUTHENTICATED_LANDING = "/chat/new";

/**
 * Resolves post-login landing href from `me.lastVisitedLocation`.
 * @example "/discovery/456/clusters?filterDays=7" or "/chat/new"
 */
export const resolveAuthenticatedLandingHref = (
  lastVisitedLocation: string | null | undefined,
): string => {
  if (
    lastVisitedLocation &&
    shouldPersistLastVisitedLocation(lastVisitedLocation)
  ) {
    return lastVisitedLocation;
  }
  return DEFAULT_AUTHENTICATED_LANDING;
};

/** Guaranteed-valid redirect for unmatched paths (never uses stored location). */
export const resolveCatchAllRedirect = (loggedIn: boolean) =>
  loggedIn
    ? {
        to: "/chat/$conversationId" as const,
        params: { conversationId: "new" as const },
      }
    : { to: "/auth/login" as const };

/**
 * Login redirect carrying the SSO return params, or `null` when there are none.
 * Current callback fragments remain fragments so one-time codes stay out of logs.
 */
export const resolveSsoReturnLoginRedirect = (search: string, hash = "") => {
  const hashReturn = readSsoReturnParams("", hash);
  if (hashReturn.code || hashReturn.error) {
    const hashParams = new URLSearchParams({
      ...(hashReturn.code && { [SSO_CODE_PARAM]: hashReturn.code }),
      ...(hashReturn.error && { [SSO_ERROR_PARAM]: hashReturn.error }),
    });

    return {
      to: "/auth/login" as const,
      hash: hashParams.toString(),
    };
  }

  const { code, error } = readSsoReturnParams(search);

  if (!code && !error) {
    return null;
  }

  return {
    to: "/auth/login" as const,
    search: {
      ...(code && { [SSO_CODE_PARAM]: code }),
      ...(error && { [SSO_ERROR_PARAM]: error }),
    },
  };
};

/**
 * Resolves landing href after login or when already authenticated on auth routes.
 * Fetches `me` when tokens exist but profile is not yet hydrated.
 */
export const getAuthenticatedLandingHref = async (): Promise<string> => {
  let lastVisited = useAuth.getState().user.last_visited_location;

  if (
    useAuth.getState().loggedIn &&
    useAuth.getState().accessToken &&
    useAuth.getState().user.id === defaultUserInfo.id
  ) {
    await fetchMe();
    lastVisited = useAuth.getState().user.last_visited_location;
  }

  return resolveAuthenticatedLandingHref(lastVisited);
};
